<template>
  <div class="da-ping-container" :style="screenStyle">
    <!-- 顶部标题栏 -->
    <div class="header">
      <div class="header-left">
        <span class="clock">{{ currentTime }}</span>
        <span class="scope-tag">销售口径：待使用 + 已完成</span>
      </div>
      <div class="header-center">
        <h1 class="title">景区数据驾驶舱</h1>
      </div>
      <div class="header-right">
        <span class="update-time">数据更新：{{ updateTime }}</span>
        <el-button
          type="primary"
          size="small"
          @click="refreshData"
          :loading="loading"
        >
          刷新
        </el-button>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 左侧栏 -->
      <div class="left-panel">
        <!-- A. 经营概览（对齐财务端取值） -->
        <div class="panel-card">
          <div class="card-title">经营概览</div>
          <div class="card-content">
            <div class="kpi-grid">
              <div class="kpi-item">
                <div class="kpi-value">
                  {{ formatMoney(data.OrderUserAnalysis.TodayAmount) }}
                </div>
                <div class="kpi-label">今日成交额</div>
              </div>
              <div class="kpi-item">
                <div class="kpi-value">
                  {{ formatMoney(data.OrderUserAnalysis.WeekAmount) }}
                </div>
                <div class="kpi-label">本周成交额</div>
              </div>
              <div class="kpi-item">
                <div class="kpi-value">
                  {{ formatMoney(data.OrderUserAnalysis.TotalAmount) }}
                </div>
                <div class="kpi-label">累计成交额</div>
              </div>
              <div class="kpi-item">
                <div
                  class="kpi-value"
                  :class="{
                    negative: Number(data.OrderUserAnalysis.Revenue) < 0,
                  }"
                >
                  {{ formatMoney(data.OrderUserAnalysis.Revenue) }}
                </div>
                <div class="kpi-label">累计营收</div>
              </div>
            </div>
            <div class="stat-row compact">
              <div class="stat-item">
                <div class="stat-value sm">
                  {{ data.OrderUserAnalysis.TodayOrderCount ?? 0 }}
                </div>
                <div class="stat-label">今日成交单</div>
              </div>
              <div class="stat-item">
                <div class="stat-value sm">
                  {{ data.OrderUserAnalysis.WeekOrderCount ?? 0 }}
                </div>
                <div class="stat-label">本周成交单</div>
              </div>
              <div class="stat-item">
                <div class="stat-value sm">
                  {{ formatMoney(data.OrderUserAnalysis.AvgOrderAmount) }}
                </div>
                <div class="stat-label">客单价</div>
              </div>
            </div>
            <div class="stat-row compact">
              <div class="stat-item">
                <div class="stat-value local">
                  {{ data.OrderUserAnalysis.LocalPercent || "-" }}
                </div>
                <div class="stat-label">本地用户</div>
              </div>
              <div class="stat-item">
                <div class="stat-value external">
                  {{ data.OrderUserAnalysis.ExternalPercent || "-" }}
                </div>
                <div class="stat-label">外地用户</div>
              </div>
            </div>
          </div>
        </div>

        <!-- B. 用户来源分布 -->
        <div class="panel-card">
          <div class="card-title">用户来源分布</div>
          <div class="card-content chart-container">
            <div ref="userSourceChart" class="chart"></div>
          </div>
        </div>

        <!-- C. 旅游资源分布 -->
        <div class="panel-card">
          <div class="card-title">旅游资源分布</div>
          <div class="card-content chart-container">
            <div ref="resourceTypeChart" class="chart"></div>
          </div>
        </div>
      </div>

      <!-- 中央栏：百度地图 -->
      <div class="center-panel">
        <div class="panel-card map-card">
          <div class="card-title">
            肥西县景区分布地图
            <span class="map-tip">鼠标悬停查看景点销售</span>
          </div>
          <div class="card-content map-content">
            <div ref="mapRef" class="baidu-map"></div>

            <!-- 地图加载失败 / 未就绪时的占位 -->
            <div v-if="mapError" class="map-fallback">
              <div class="fallback-title">地图加载失败</div>
              <div class="fallback-desc">
                请检查网络或百度地图 AK 配置；下方为景点销售数据
              </div>
              <div class="fallback-list">
                <div
                  v-for="item in data.ScenicSpots"
                  :key="item.SpId"
                  class="fallback-item"
                >
                  <span class="fi-name">{{ item.Name }}</span>
                  <span class="fi-val"
                    >{{ item.SalesCount || 0 }} 票 /
                    {{ formatMoney(item.Amount) }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- D. 景点热度 TOP5 -->
        <div class="panel-card top5-card">
          <div class="card-title">景点热度 TOP5</div>
          <div class="card-content top5-content">
            <div class="scenic-list">
              <div
                v-for="(item, index) in data.ScenicSpotTop5"
                :key="item.SpId || index"
                class="scenic-item"
                :class="{ active: hoverSpotId === item.SpId }"
                @mouseenter="focusSpot(item)"
                @mouseleave="hoverSpotId = null"
              >
                <div class="scenic-rank" :class="'rank-' + (index + 1)">
                  {{ index + 1 }}
                </div>
                <div class="scenic-info">
                  <div class="scenic-name">{{ item.Name || "-" }}</div>
                  <div class="scenic-coord">
                    {{ item.OrderCount || 0 }} 单 ·
                    {{ formatMoney(item.Amount) }}
                  </div>
                </div>
                <div class="scenic-sales">{{ item.SalesCount || 0 }} 票</div>
              </div>
              <div
                v-if="!data.ScenicSpotTop5 || data.ScenicSpotTop5.length === 0"
                class="empty-tip"
              >
                暂无数据（需在商品-门票扩展中配置经纬度）
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧栏 -->
      <div class="right-panel">
        <!-- E. 热销产品月度趋势 -->
        <div class="panel-card">
          <div class="card-title">热销产品月度趋势</div>
          <div class="card-content chart-container">
            <div ref="hotProductChart" class="chart"></div>
          </div>
        </div>

        <!-- F. 产品销售排行榜 -->
        <div class="panel-card">
          <div class="card-title">产品销售排行榜</div>
          <div class="card-content">
            <div class="rank-list">
              <div
                v-for="(item, index) in data.ProductSalesRank"
                :key="index"
                class="rank-item"
              >
                <div class="rank-num" :class="'num-' + (index + 1)">
                  {{ index + 1 }}
                </div>
                <div class="rank-main">
                  <div class="rank-name">{{ item.Name || "-" }}</div>
                  <div class="rank-bar">
                    <div
                      class="rank-bar-fill"
                      :style="{ width: getBarWidth(item.SalesCount) + '%' }"
                    ></div>
                  </div>
                </div>
                <div class="rank-value">
                  <div>{{ item.SalesCount || 0 }}</div>
                  <div class="rank-amount">{{ formatMoney(item.Amount) }}</div>
                </div>
              </div>
              <div
                v-if="
                  !data.ProductSalesRank || data.ProductSalesRank.length === 0
                "
                class="empty-tip"
              >
                暂无数据
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 显示比例微调：Teleport 到 body，固定挂在窗口右下角，不受画面缩放影响 -->
    <Teleport to="body">
      <div class="screen-tool" :class="{ hint: showScaleHint }">
        <div v-if="showScaleTool" class="screen-tool-panel">
          <div class="st-row">
            <span class="st-label">比例</span>
            <input
              class="st-range"
              type="range"
              min="50"
              max="150"
              step="5"
              :value="Math.round(scaleFactor * 100)"
              @input="setScaleFactor($event.target.value / 100)"
            />
            <span class="st-value">{{ Math.round(scaleFactor * 100) }}%</span>
          </div>
          <div class="st-row st-actions">
            <button class="st-btn" @click="resetScale">自适应</button>
            <button class="st-btn" @click="showScaleTool = false">收起</button>
          </div>
        </div>
        <button
          class="screen-tool-btn"
          :class="{ active: showScaleTool }"
          title="显示比例（双击恢复自适应）"
          @click="showScaleTool = !showScaleTool"
          @dblclick="resetScale"
        >
          <el-icon :size="12"><Setting /></el-icon>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script>
import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
} from "vue";
import * as echarts from "echarts";
import $http from "@/http/index";

/** 百度地图 AK（浏览器端） */
const BAIDU_AK = "sSKEgajloner5cCa768bj3bBn9G45oWx";

/** 设计稿尺寸：所有布局按 1920×1080 排版，再整体缩放铺满屏幕 */
const DESIGN_WIDTH = 1920;
const DESIGN_HEIGHT = 1080;

/**
 * 缩放模式：
 *   fill    = 拉伸铺满整个窗口，无黑边（非 16:9 屏幕会有轻微变形，大屏项目常用）
 *   contain = 等比缩放居中，保证完全不变形（非 16:9 屏幕会留黑边）
 */
const FIT_MODE = "fill";

/** 默认中心点：肥西县三河古镇 */
const DEFAULT_CENTER = { lng: 117.2535, lat: 31.5234 };

/** 默认缩放级别：18 为百度地图可用的最大级别（比例尺最大、最清晰）；改小可看更大范围 */
const DEFAULT_ZOOM = 18;

/**
 * 坐标来源：数据库 ShangPinMenPiaoExtend.Jd(经度) / Wd(纬度)
 * 若这些坐标是用「百度坐标拾取器」采集的，保持 false；
 * 若来自高德/腾讯/手机定位（GCJ-02），百度地图上会偏约 500 米，此时改为 true。
 */
const NEED_GCJ02_TO_BD09 = false;

/** GCJ-02 -> BD-09 坐标转换 */
const gcj02ToBd09 = (lng, lat) => {
  const xPi = (lng * Math.PI * 3000.0) / 180.0;
  const yPi = (lat * Math.PI * 3000.0) / 180.0;
  const z = Math.sqrt(lng * lng + lat * lat) + 0.00002 * Math.sin(yPi);
  const theta = Math.atan2(lat, lng) + 0.000003 * Math.cos(xPi);
  return {
    lng: z * Math.cos(theta) + 0.0065,
    lat: z * Math.sin(theta) + 0.006,
  };
};

const toBMapPoint = (lng, lat) => {
  if (lng == null || lat == null) return null;
  const p = NEED_GCJ02_TO_BD09
    ? gcj02ToBd09(Number(lng), Number(lat))
    : { lng: Number(lng), lat: Number(lat) };
  return { lng: p.lng, lat: p.lat };
};

/** 悬停提示气泡内容（内联样式：InfoWindow 在组件作用域外） */
const buildTipHtml = (item) => {
  const amount = formatMoney(item.Amount);
  return `
    <div style="min-width:190px;padding:2px 2px 0;color:#e8f4ff;font-size:12px;line-height:1.7;">
      <div style="font-size:14px;font-weight:700;color:#00f6ff;margin-bottom:6px;">${escapeHtml(
        item.Name || "-"
      )}</div>
      <div>销量：<b style="color:#00ffa3;">${item.SalesCount || 0}</b> 票</div>
      <div>订单：<b style="color:#ffd93d;">${item.OrderCount || 0}</b> 单</div>
      <div>销售额：<b style="color:#00d4ff;">${amount}</b></div>
      <div style="margin-top:4px;color:#7f96b3;font-size:11px;">坐标：${Number(
        item.Jd
      ).toFixed(4)}, ${Number(item.Wd).toFixed(4)}</div>
    </div>`;
};

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (m) => {
    return {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    }[m];
  });
}

function formatMoney(v) {
  const n = Number(v || 0);
  if (!n) return "0";
  if (Math.abs(n) >= 10000) return (n / 10000).toFixed(2) + " 万";
  return n.toFixed(2) + " 元";
}

/** 动态加载百度地图 JS API（v3.0） */
let bmapLoader = null;
function loadBaiduMap() {
  if (window.BMap) return Promise.resolve(window.BMap);
  if (bmapLoader) return bmapLoader;

  bmapLoader = new Promise((resolve, reject) => {
    const cbName = "__baiduMapCallback__";
    window[cbName] = () => resolve(window.BMap);
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = `https://api.map.baidu.com/api?v=3.0&ak=${BAIDU_AK}&s=1&callback=${cbName}`;
    script.onerror = () => reject(new Error("百度地图脚本加载失败"));
    document.head.appendChild(script);
    // 兜底超时
    setTimeout(() => {
      if (!window.BMap) reject(new Error("百度地图脚本加载超时"));
    }, 12000);
  });
  return bmapLoader;
}

export default defineComponent({
  name: "DaPing",
  setup() {
    const data = ref({
      OrderUserAnalysis: {},
      UserSourceDistribution: [],
      ResourceTypeDistribution: [],
      ScenicSpotTop5: [],
      ScenicSpots: [],
      HotProductTrend: {
        Months: [],
        MenPiaoSales: [],
        DaoYouSales: [],
        WenChuangSales: [],
      },
      ProductSalesRank: [],
      UpdateTime: "",
    });

    const loading = ref(false);
    const currentTime = ref("");
    const updateTime = ref("");
    const mapError = ref(false);
    const hoverSpotId = ref(null);

    const userSourceChart = ref(null);
    const resourceTypeChart = ref(null);
    const hotProductChart = ref(null);
    const mapRef = ref(null);

    /* ===== 整体缩放：按 1920×1080 设计稿排版，缩放铺满当前窗口 ===== */
    const viewport = ref({
      w: window.innerWidth || DESIGN_WIDTH,
      h: window.innerHeight || DESIGN_HEIGHT,
    });

    /** 手动微调比例（1 = 铺满自适应），保存在本地，下次打开沿用 */
    const SCALE_STORE_KEY = "daping_scale_factor";
    const scaleFactor = ref(Number(localStorage.getItem(SCALE_STORE_KEY)) || 1);
    const showScaleTool = ref(false);
    /** 进入页面后先亮几秒提示这里有个设置，随后自动淡出 */
    const showScaleHint = ref(true);
    let scaleHintTimer = null;
    const setScaleFactor = (v) => {
      const n = Math.min(1.5, Math.max(0.5, Number(v) || 1));
      scaleFactor.value = n;
      localStorage.setItem(SCALE_STORE_KEY, String(n));
    };
    const resetScale = () => setScaleFactor(1);

    // 自适应缩放比（不含手动系数）
    const screenScaleXY = computed(() => {
      const { w, h } = viewport.value;
      const sx = w / DESIGN_WIDTH;
      const sy = h / DESIGN_HEIGHT;
      return FIT_MODE === "fill"
        ? { x: sx, y: sy }
        : { x: Math.min(sx, sy), y: Math.min(sx, sy) };
    });
    // 最终生效的缩放比 = 自适应 × 手动系数
    const renderScale = computed(() => {
      const base = screenScaleXY.value;
      const f = scaleFactor.value;
      return { x: base.x * f, y: base.y * f };
    });
    // 缩放后是否超出窗口（超出则允许浏览器滚动条，能滚到被裁掉的部分）
    const overflowXY = computed(() => {
      const { x, y } = renderScale.value;
      const { w, h } = viewport.value;
      return {
        x: DESIGN_WIDTH * x > w + 1,
        y: DESIGN_HEIGHT * y > h + 1,
      };
    });
    const screenStyle = computed(() => {
      const { x, y } = renderScale.value;
      const { w, h } = viewport.value;
      const of = overflowXY.value;
      return {
        transform: `scale(${x}, ${y})`,
        transformOrigin: "left top",
        // 放得下就居中（四周留边）；放不下就贴左上角，让滚动条能滚到剩余部分
        left: (of.x ? 0 : (w - DESIGN_WIDTH * x) / 2) + "px",
        top: (of.y ? 0 : (h - DESIGN_HEIGHT * y) / 2) + "px",
      };
    });
    // 窗口/比例变化时切换页面滚动：超出 -> 出滚动条，放得下 -> 恢复隐藏并回到左上角
    const applyPageScroll = () => {
      const of = overflowXY.value;
      const v = of.x || of.y ? "auto" : "";
      document.documentElement.style.overflow = v;
      document.body.style.overflow = v;
      if (!v) window.scrollTo(0, 0);
    };
    watch([renderScale, viewport], applyPageScroll, { immediate: true });
    const updateScreenScale = () => {
      viewport.value = {
        w: window.innerWidth,
        h: window.innerHeight,
      };
    };
    // 画布按“设计稿尺寸 × 缩放比”渲染，缩放后文字/折线不发虚
    const chartDpr = () =>
      Math.max(
        1,
        (window.devicePixelRatio || 1) *
          Math.max(renderScale.value.x, renderScale.value.y)
      );

    let userSourceChartInstance = null;
    let resourceTypeChartInstance = null;
    let hotProductChartInstance = null;
    let mapInstance = null;
    let infoWindow = null;

    let viewFitted = false;
    let timer = null;
    let clockTimer = null;

    const updateClock = () => {
      const now = new Date();
      currentTime.value = now.toLocaleString("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    };

    const applyData = async (payload) => {
      if (!payload) return;
      data.value = payload;
      updateTime.value = payload.UpdateTime
        ? new Date(payload.UpdateTime).toLocaleString("zh-CN")
        : "";
      await nextTick();
      initCharts();
      await initMap();
    };

    const fetchData = async () => {
      try {
        loading.value = true;
        const res = await $http.get("/JingQuDaPing/GetData");
        await applyData(res.data);
      } catch (error) {
        console.error("获取大屏数据失败:", error);
      } finally {
        loading.value = false;
      }
    };

    const refreshData = async () => {
      try {
        loading.value = true;
        const res = await $http.post("/JingQuDaPing/Refresh");
        await applyData(res.data);
      } catch (error) {
        console.error("刷新大屏数据失败:", error);
      } finally {
        loading.value = false;
      }
    };

    const getBarWidth = (salesCount) => {
      const list = data.value.ProductSalesRank || [];
      if (list.length === 0) return 0;
      const maxSales = Math.max(
        ...list.map((item) => Number(item.SalesCount) || 0)
      );
      if (!maxSales) return 0;
      return (Number(salesCount || 0) / maxSales) * 100;
    };

    /* ======================= 图表 ======================= */

    const initCharts = () => {
      initUserSourceChart();
      initResourceTypeChart();
      initHotProductChart();
    };

    const initUserSourceChart = () => {
      if (!userSourceChart.value) return;
      if (userSourceChartInstance) userSourceChartInstance.dispose();
      userSourceChartInstance = echarts.init(userSourceChart.value, null, {
        devicePixelRatio: chartDpr(),
      });

      const chartData = data.value.UserSourceDistribution || [];
      const dates = chartData.map((item) => item.Date);
      const localData = chartData.map((item) => item.LocalCount);
      const externalData = chartData.map((item) => item.ExternalCount);

      userSourceChartInstance.setOption({
        tooltip: { trigger: "axis" },
        legend: {
          data: ["本地", "外地"],
          textStyle: { color: "#fff", fontSize: 10 },
          top: 0,
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          top: 30,
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: dates,
          axisLabel: { color: "#fff", fontSize: 10 },
          axisLine: { lineStyle: { color: "#333" } },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          axisLabel: { color: "#fff", fontSize: 10 },
          splitLine: { lineStyle: { color: "#333" } },
        },
        series: [
          {
            name: "本地",
            type: "bar",
            data: localData,
            itemStyle: { color: "#00d4ff" },
          },
          {
            name: "外地",
            type: "bar",
            data: externalData,
            itemStyle: { color: "#ff6b6b" },
          },
        ],
      });
    };

    const initResourceTypeChart = () => {
      if (!resourceTypeChart.value) return;
      if (resourceTypeChartInstance) resourceTypeChartInstance.dispose();
      resourceTypeChartInstance = echarts.init(resourceTypeChart.value, null, {
        devicePixelRatio: chartDpr(),
      });

      const chartData = data.value.ResourceTypeDistribution || [];

      resourceTypeChartInstance.setOption({
        tooltip: { trigger: "item" },
        legend: {
          orient: "vertical",
          right: 10,
          top: "center",
          textStyle: { color: "#fff", fontSize: 10 },
        },
        series: [
          {
            type: "pie",
            radius: ["40%", "70%"],
            center: ["38%", "50%"],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 5,
              borderColor: "#0a1929",
              borderWidth: 2,
            },
            label: {
              show: true,
              color: "#fff",
              fontSize: 10,
              formatter: "{b}\n{c}",
            },
            data: chartData.map((item, index) => ({
              value: item.Count,
              name: item.Name,
              itemStyle: {
                color: [
                  "#00d4ff",
                  "#ff6b6b",
                  "#ffd93d",
                  "#6bcb77",
                  "#4d96ff",
                  "#ff6b9d",
                ][index % 6],
              },
            })),
          },
        ],
      });
    };

    const initHotProductChart = () => {
      if (!hotProductChart.value) return;
      if (hotProductChartInstance) hotProductChartInstance.dispose();
      hotProductChartInstance = echarts.init(hotProductChart.value, null, {
        devicePixelRatio: chartDpr(),
      });

      const trend = data.value.HotProductTrend || {};
      const months = trend.Months || [];
      const menPiao = trend.MenPiaoSales || [];
      const daoYou = trend.DaoYouSales || [];
      const wenChuang = trend.WenChuangSales || [];

      hotProductChartInstance.setOption({
        tooltip: { trigger: "axis" },
        legend: {
          data: ["门票", "导览", "文创"],
          textStyle: { color: "#fff", fontSize: 10 },
          top: 0,
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          top: 30,
          containLabel: true,
        },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: months,
          axisLabel: { color: "#fff", fontSize: 10 },
          axisLine: { lineStyle: { color: "#333" } },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          axisLabel: { color: "#fff", fontSize: 10 },
          splitLine: { lineStyle: { color: "#333" } },
        },
        series: [
          {
            name: "门票",
            type: "line",
            data: menPiao,
            itemStyle: { color: "#00d4ff" },
            smooth: true,
          },
          {
            name: "导览",
            type: "line",
            data: daoYou,
            itemStyle: { color: "#ffd93d" },
            smooth: true,
          },
          {
            name: "文创",
            type: "line",
            data: wenChuang,
            itemStyle: { color: "#ff6b9d" },
            smooth: true,
          },
        ],
      });
    };

    /* ======================= 百度地图 ======================= */

    /** 引线气泡：{ key, point, boxEl, lineEl, manual, offsetX, offsetY } */
    let calloutItems = [];
    /** 用户手动拖过的气泡位置（key -> {x, y}，相对坐标点的像素偏移），刷新后保持不变 */
    const manualOffsets = new Map();
    /** 被用户点 × 关掉的气泡（key 集合），本次会话内不再显示 */
    const hiddenSpotKeys = new Set();
    /** 当前正在拖拽的气泡 */
    let dragState = null;

    const clearCallouts = () => {
      calloutItems.forEach((it) => {
        it.boxEl.remove();
        it.lineEl.remove();
      });
      calloutItems = [];
      dragState = null;
    };

    /** 设置气泡与引线位置（left/top 为相对地图左上角的像素） */
    const placeCallout = (it, px, left, top) => {
      const boxW = it.boxEl.offsetWidth || 140;
      const boxH = it.boxEl.offsetHeight || 34;
      it.boxEl.style.left = `${left}px`;
      it.boxEl.style.top = `${top}px`;
      it.boxEl.style.visibility = "visible";

      // 引线：从坐标点连到气泡靠坐标点的那条边
      const startX = px.x;
      const startY = px.y;
      const endX = left > px.x ? left : left + boxW;
      const endY = top + boxH / 2;
      const dx = endX - startX;
      const dy = endY - startY;
      const len = Math.sqrt(dx * dx + dy * dy);
      const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

      it.lineEl.style.left = `${startX}px`;
      it.lineEl.style.top = `${startY}px`;
      it.lineEl.style.width = `${len}px`;
      it.lineEl.style.transform = `rotate(${angle}deg)`;
      it.lineEl.style.transformOrigin = "0 0";
      it.lineEl.style.visibility = "visible";
    };

    /** 拖拽结束：记录偏移，刷新后仍保持用户摆放的位置 */
    const endDrag = () => {
      if (!dragState) return;
      const it = dragState.item;
      dragState = null;
      it.boxEl.classList.remove("dragging");
      if (mapInstance) mapInstance.enableDragging();

      const px = mapInstance.pointToOverlayPixel(it.point);
      it.manual = true;
      it.offsetX = (parseFloat(it.boxEl.style.left) || 0) - px.x;
      it.offsetY = (parseFloat(it.boxEl.style.top) || 0) - px.y;
      manualOffsets.set(it.key, { x: it.offsetX, y: it.offsetY });
      layoutCallouts();
    };

    const onDocMouseMove = (e) => {
      if (!dragState || !mapInstance) return;
      const { item, startX, startY, originLeft, originTop } = dragState;
      // 整个大屏做了 transform: scale 缩放，鼠标位移需换算回设计稿像素
      const { x, y } = renderScale.value;
      const left = originLeft + (e.clientX - startX) / (x || 1);
      const top = originTop + (e.clientY - startY) / (y || 1);
      const px = mapInstance.pointToOverlayPixel(item.point);
      placeCallout(item, px, left, top);
    };

    /**
     * 在坐标旁创建"引线 + 气泡"
     * 位置默认由 layoutCallouts() 排布；拖过的气泡用 manualOffsets 里的偏移
     */
    const buildCallout = (point, spot) => {
      if (!mapInstance) return;
      const key = spot.key || `${spot.Jd},${spot.Wd}`;
      if (hiddenSpotKeys.has(key)) return;

      const pane = mapInstance.getPanes().floatPane;

      const lineEl = document.createElement("div");
      lineEl.className = "dp-callout-line";
      pane.appendChild(lineEl);

      const boxEl = document.createElement("div");
      boxEl.className = "dp-callout-box";
      const sales = Number(spot.SalesCount) || 0;
      boxEl.innerHTML =
        `<div class="cb-name">${escapeHtml(spot.Name || "-")}</div>` +
        `<div class="cb-val">${sales} 票 · ${formatMoney(spot.Amount)}</div>` +
        `<div class="cb-close" title="删除">×</div>`;

      // 拖拽移动
      boxEl.addEventListener("mousedown", (e) => {
        if (e.button !== 0) return;
        if (e.target.classList.contains("cb-close")) return;
        e.stopPropagation();
        e.preventDefault();
        dragState = {
          item,
          startX: e.clientX,
          startY: e.clientY,
          originLeft: parseFloat(boxEl.style.left) || 0,
          originTop: parseFloat(boxEl.style.top) || 0,
        };
        boxEl.classList.add("dragging");
        mapInstance.disableDragging();
      });

      // 右上角 × 删除
      boxEl.querySelector(".cb-close").addEventListener("click", (e) => {
        e.stopPropagation();
        hiddenSpotKeys.add(key);
        manualOffsets.delete(key);
        calloutItems = calloutItems.filter((i) => i !== item);
        boxEl.remove();
        lineEl.remove();
        layoutCallouts();
      });

      // 点气泡时联动左侧列表高亮
      boxEl.addEventListener("mouseenter", () => {
        hoverSpotId.value = spot.SpId;
      });
      boxEl.addEventListener("mouseleave", () => {
        hoverSpotId.value = null;
      });
      pane.appendChild(boxEl);

      const manual = manualOffsets.get(key);
      const item = {
        key,
        point,
        boxEl,
        lineEl,
        manual: !!manual,
        offsetX: manual ? manual.x : 0,
        offsetY: manual ? manual.y : 0,
      };
      calloutItems.push(item);
    };

    /**
     * 统一排布所有引线气泡：
     * 1) 气泡固定放在锚点右侧（右侧放不下则放左侧）
     * 2) 按锚点纵坐标排序，逐个下移，保证上下至少 6px 间距，不重叠
     * 3) 引线从锚点连到气泡内侧边
     */
    const layoutCallouts = () => {
      if (!mapInstance || calloutItems.length === 0) return;
      const size = mapInstance.getSize();

      const items = calloutItems
        .map((it) => ({ it, px: mapInstance.pointToOverlayPixel(it.point) }))
        .filter(
          (o) =>
            o.px.x > -300 &&
            o.px.x < size.width + 300 &&
            o.px.y > -300 &&
            o.px.y < size.height + 300
        );

      // 视野外的气泡先隐藏
      calloutItems.forEach((it) => {
        it.boxEl.style.visibility = "hidden";
        it.lineEl.style.visibility = "hidden";
      });

      items.sort((a, b) => a.px.y - b.px.y);

      const GAP = 6;
      const OFFSET = 42;
      let lastBottom = -Infinity;

      items.forEach(({ it, px }) => {
        const boxW = it.boxEl.offsetWidth || 140;
        const boxH = it.boxEl.offsetHeight || 34;

        // 用户拖过的气泡：按记录的偏移还原，不参与自动排布
        if (it.manual) {
          const left = Math.max(
            4,
            Math.min(px.x + it.offsetX, size.width - boxW - 4)
          );
          const top = Math.max(
            4,
            Math.min(px.y + it.offsetY, size.height - boxH - 4)
          );
          placeCallout(it, px, left, top);
          return;
        }

        let left = px.x + OFFSET;
        if (left + boxW > size.width - 8) {
          left = px.x - OFFSET - boxW;
        }
        let top = px.y - boxH / 2;
        if (top < lastBottom + GAP) top = lastBottom + GAP;
        top = Math.max(4, Math.min(top, size.height - boxH - 4));
        lastBottom = top + boxH;

        placeCallout(it, px, left, top);
      });
    };

    const clearMarkers = () => {
      if (!mapInstance) return;
      clearCallouts();
      if (infoWindow) {
        mapInstance.closeInfoWindow(infoWindow);
      }
    };

    const buildMarkers = () => {
      // 后端新版本返回 ScenicSpots（全部有坐标的景点）；
      // 若接口还是旧版本（未重启服务）则退回 ScenicSpotTop5，避免地图上完全没有锚点
      const allSpots =
        data.value.ScenicSpots && data.value.ScenicSpots.length > 0
          ? data.value.ScenicSpots
          : data.value.ScenicSpotTop5 || [];

      const validSpots = allSpots.filter(
        (s) => s && s.Jd != null && s.Wd != null
      );

      if (!data.value.ScenicSpots || data.value.ScenicSpots.length === 0) {
        console.warn(
          "[大屏] 接口未返回 ScenicSpots（可能后端未重启），已回退使用 ScenicSpotTop5，锚点数量：",
          validSpots.length
        );
      }

      // 同一坐标存在多张门票（如 spId 36/37 两个联票坐标完全相同）时合并为一个锚点，
      // 否则多个锚点叠在一起会出现“重影”
      const grouped = new Map();
      validSpots.forEach((s) => {
        const key = `${Number(s.Jd).toFixed(6)},${Number(s.Wd).toFixed(6)}`;
        const exist = grouped.get(key);
        const salesCount = Number(s.SalesCount) || 0;
        const orderCount = Number(s.OrderCount) || 0;
        const amount = Number(s.Amount) || 0;
        if (exist) {
          exist.names.push(s.Name);
          exist.SalesCount += salesCount;
          exist.OrderCount += orderCount;
          exist.Amount += amount;
        } else {
          grouped.set(key, {
            key,
            SpId: s.SpId,
            Jd: s.Jd,
            Wd: s.Wd,
            names: [s.Name],
            SalesCount: salesCount,
            OrderCount: orderCount,
            Amount: amount,
          });
        }
      });

      // 合并后的景点（Name 取第一个票种名，多票种时在名称后注明数量），销量从高到低
      const spots = Array.from(grouped.values())
        .map((g) => ({
          ...g,
          Name:
            g.names.length > 1
              ? `${g.names[0]} 等 ${g.names.length} 个票种`
              : g.names[0],
        }))
        .sort((a, b) => b.SalesCount - a.SalesCount);

      if (spots.length === 0) {
        mapInstance.centerAndZoom(
          new window.BMap.Point(DEFAULT_CENTER.lng, DEFAULT_CENTER.lat),
          DEFAULT_ZOOM
        );
        return;
      }

      const points = [];

      spots.forEach((spot) => {
        const p = toBMapPoint(spot.Jd, spot.Wd);
        if (!p) return;
        const point = new window.BMap.Point(p.lng, p.lat);
        points.push(point);

        // 不再绘制锚点图标，直接用引线气泡标注坐标
        buildCallout(point, spot);
      });

      // 首次加载：定位到销量最高的景点，使用最大缩放级别（不自动适配全景点）
      if (!viewFitted && points.length > 0) {
        mapInstance.centerAndZoom(points[0], DEFAULT_ZOOM);
        viewFitted = true;
      }

      layoutCallouts();
    };

    const initMap = async () => {
      if (!mapRef.value) return;

      try {
        const BMap = await loadBaiduMap();
        if (!BMap) throw new Error("百度地图未就绪");

        if (!mapInstance) {
          mapInstance = new BMap.Map(mapRef.value, {
            enableMapClick: false,
          });
          mapInstance.centerAndZoom(
            new BMap.Point(DEFAULT_CENTER.lng, DEFAULT_CENTER.lat),
            DEFAULT_ZOOM
          );
          mapInstance.enableScrollWheelZoom(true);
          // 注意：不要用 setMapStyle({style:'dark'})！
          // 该 AK 未开通「个性化地图」权限，dark 样式会请求 customimage/tile 并全部失败（底图变全黑）。
          // 暗色效果改由 CSS filter 实现，见 .baidu-map 样式。
          const anchorBR = window.BMAP_ANCHOR_BOTTOM_RIGHT || 3;
          const anchorBL = window.BMAP_ANCHOR_BOTTOM_LEFT || 1;
          mapInstance.addControl(
            new BMap.NavigationControl({ anchor: anchorBR })
          );
          mapInstance.addControl(new BMap.ScaleControl({ anchor: anchorBL }));

          infoWindow = new BMap.InfoWindow("", {
            offset: new BMap.Size(0, -30),
            enableMessage: false,
          });

          // 缩放时收起气泡，避免错位
          mapInstance.addEventListener("zoomstart", () => {
            mapInstance.closeInfoWindow(infoWindow);
          });

          // 地图移动 / 缩放后重排引线气泡
          ["moving", "moveend", "zoomend", "resize"].forEach((evt) => {
            mapInstance.addEventListener(evt, layoutCallouts);
          });
        }

        clearMarkers();
        buildMarkers();
        mapError.value = false;
      } catch (err) {
        console.error("百度地图初始化失败:", err);
        mapError.value = true;
      }
    };

    /** 列表悬停时联动地图打点 */
    const focusSpot = (spot) => {
      hoverSpotId.value = spot.SpId;
      if (!mapInstance || !window.BMap || spot.Jd == null || spot.Wd == null)
        return;
      const p = toBMapPoint(spot.Jd, spot.Wd);
      if (!p) return;
      const point = new window.BMap.Point(p.lng, p.lat);
      infoWindow.setContent(
        `<div style="background:rgba(6,20,34,0.94);border:1px solid rgba(0,212,255,0.55);
          border-radius:6px;padding:10px 12px;">${buildTipHtml(spot)}</div>`
      );
      mapInstance.openInfoWindow(infoWindow, point);
    };

    const handleResize = () => {
      updateScreenScale();
      userSourceChartInstance?.resize();
      resourceTypeChartInstance?.resize();
      hotProductChartInstance?.resize();
    };

    // 手动调比例后按新的缩放比重绘图表，避免放大后发虚
    watch(scaleFactor, () => {
      if (userSourceChart.value) initCharts();
    });

    onMounted(() => {
      // 先算好缩放比，再初始化图表/地图，保证按设计稿尺寸取容器大小
      updateScreenScale();
      // 右下角设置按钮先亮 4 秒，方便发现，之后自动隐去
      scaleHintTimer = setTimeout(() => {
        showScaleHint.value = false;
      }, 4000);
      updateClock();
      clockTimer = setInterval(updateClock, 1000);
      fetchData();
      timer = setInterval(fetchData, 30000);
      window.addEventListener("resize", handleResize);
      // 气泡拖拽
      document.addEventListener("mousemove", onDocMouseMove);
      document.addEventListener("mouseup", endDrag);
    });

    onUnmounted(() => {
      if (timer) clearInterval(timer);
      if (clockTimer) clearInterval(clockTimer);
      if (scaleHintTimer) clearTimeout(scaleHintTimer);
      // 恢复页面滚动状态，避免影响其他页面
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousemove", onDocMouseMove);
      document.removeEventListener("mouseup", endDrag);
      userSourceChartInstance?.dispose();
      resourceTypeChartInstance?.dispose();
      hotProductChartInstance?.dispose();
      if (mapInstance) {
        mapInstance.clearOverlays();
        mapInstance = null;
      }
    });

    return {
      data,
      loading,
      currentTime,
      updateTime,
      mapError,
      hoverSpotId,
      userSourceChart,
      resourceTypeChart,
      hotProductChart,
      mapRef,
      refreshData,
      getBarWidth,
      formatMoney,
      focusSpot,
      screenStyle,
      scaleFactor,
      showScaleTool,
      showScaleHint,
      setScaleFactor,
      resetScale,
    };
  },
});
</script>

<style scoped>
/*
 * 固定 1920×1080 设计稿尺寸，由 JS 计算 transform: scale() 缩放到实际屏幕，
 * 任何分辨率下观感一致（缩放样式全部作用于本组件内部，不影响后台其他页面）
 */
.da-ping-container {
  position: absolute;
  left: 0;
  top: 0;
  width: 1920px;
  height: 1080px;
  background: linear-gradient(135deg, #0a1929 0%, #1a2942 50%, #0a1929 100%);
  color: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.header {
  height: 60px;
  flex: 0 0 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);
}

.header-left {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-center {
  flex: 2;
  text-align: center;
}

.header-right {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;
}

.title {
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  background: linear-gradient(90deg, #00d4ff, #00ff88);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.clock,
.update-time {
  font-size: 14px;
  color: #00d4ff;
}

.scope-tag {
  font-size: 11px;
  color: #7f96b3;
  border: 1px solid rgba(0, 212, 255, 0.25);
  border-radius: 10px;
  padding: 1px 8px;
}

.main-content {
  flex: 1;
  display: grid;
  /* 用百分比而非 vw，内嵌在后台标签页里也能按可用宽度自适应 */
  grid-template-columns: minmax(0, 22%) minmax(0, 1fr) minmax(0, 22%);
  gap: 15px;
  padding: 15px;
  overflow: hidden;
  min-height: 0;
}

.left-panel,
.right-panel {
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow: hidden;
  min-height: 0;
}

.center-panel {
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow: hidden;
  min-height: 0;
}

.panel-card {
  background: rgba(10, 25, 41, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.left-panel .panel-card:nth-child(1) {
  flex: 0 0 auto;
}

.left-panel .panel-card:nth-child(2),
.left-panel .panel-card:nth-child(3),
.right-panel .panel-card {
  flex: 1;
}

/* 地图占剩余高度，且允许被压缩（min-height:0），避免把下方 TOP5 挤没 */
.map-card {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
}

/* 景点热度 TOP5：按内容高度自适应，5 条榜单完整显示，地图随之变矮 */
.top5-card {
  flex: 0 0 auto;
}

.top5-content {
  overflow: hidden;
}

.top5-content .scenic-item {
  padding: 6px 10px;
}

.top5-content .scenic-list {
  gap: 6px;
}

.card-title {
  padding: 12px 15px;
  font-size: 16px;
  font-weight: bold;
  color: #00d4ff;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  background: rgba(0, 212, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.map-tip {
  font-size: 11px;
  font-weight: normal;
  color: #7f96b3;
}

.card-content {
  padding: 15px;
  flex: 1;
  overflow: auto;
  min-height: 0;
}

.map-content {
  padding: 0;
  position: relative;
  overflow: hidden;
}

.baidu-map {
  width: 100%;
  height: 100%;
  min-height: 320px;
  background: #0f1a26;
}

/*
 * 暗色化：只给「栅格瓦片」加反色滤镜（不依赖「个性化地图」权限）
 * - 瓦片是 <img>（http/https 的底图切图），锚点图标是 data:image，天然被排除，
 *   这样红色锚点、景点标签、悬停气泡都不会被反色，颜色保持鲜艳
 * - invert 用 0.88 而不是 1：整反会把近白的陆地压成纯黑，太暗
 * - 若还要更亮/更暗，只调 brightness（1.45 为当前基准）
 */
.baidu-map :deep(img:not([src^="data:"])) {
  filter: invert(0.88) hue-rotate(180deg) saturate(1.15) brightness(1.45)
    contrast(0.92);
}

/* 隐藏百度地图 logo/版权遮挡（保留必要版权信息，可删除此行） */
.baidu-map :deep(.BMap_cpyCtrl),
.baidu-map :deep(.anchorBL) {
  opacity: 0.45;
}

.map-fallback {
  position: absolute;
  inset: 0;
  background: rgba(6, 20, 34, 0.96);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 30px;
}

.fallback-title {
  font-size: 16px;
  color: #ff6b6b;
}

.fallback-desc {
  font-size: 12px;
  color: #7f96b3;
}

.fallback-list {
  width: 100%;
  max-width: 520px;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fallback-item {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #cfe6ff;
  border-bottom: 1px dashed rgba(0, 212, 255, 0.15);
  padding-bottom: 4px;
}

.fi-name {
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fi-val {
  color: #00d4ff;
}

.chart-container {
  padding: 10px;
}

.chart {
  width: 100%;
  height: 100%;
  min-height: 150px;
}

.kpi-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
}

.kpi-item {
  background: rgba(0, 212, 255, 0.06);
  border: 1px solid rgba(0, 212, 255, 0.15);
  border-radius: 6px;
  padding: 8px 10px;
  text-align: center;
}

.kpi-value {
  font-size: 20px;
  font-weight: bold;
  color: #00ffa3;
  white-space: nowrap;
}

.kpi-value.negative {
  color: #ff6b6b;
}

.kpi-label {
  font-size: 11px;
  color: #8aa4bd;
  margin-top: 3px;
}

.stat-row {
  display: flex;
  justify-content: space-around;
  margin-bottom: 10px;
}

.stat-row.compact {
  margin-bottom: 8px;
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 22px;
  font-weight: bold;
  color: #fff;
}

.stat-value.sm {
  font-size: 16px;
  color: #cfe6ff;
}

.stat-value.local {
  color: #00d4ff;
}

.stat-value.external {
  color: #ff6b6b;
}

.stat-label {
  font-size: 12px;
  color: #888;
  margin-top: 4px;
}

.scenic-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.scenic-item {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  background: rgba(0, 212, 255, 0.05);
  border-radius: 5px;
  border: 1px solid rgba(0, 212, 255, 0.1);
  transition: all 0.2s;
  cursor: pointer;
}

.scenic-item.active,
.scenic-item:hover {
  border-color: rgba(0, 212, 255, 0.6);
  background: rgba(0, 212, 255, 0.12);
}

.scenic-rank {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 12px;
  margin-right: 12px;
  background: rgba(255, 255, 255, 0.1);
}

.scenic-rank.rank-1 {
  background: #ffd93d;
  color: #000;
}

.scenic-rank.rank-2 {
  background: #c0c0c0;
  color: #000;
}

.scenic-rank.rank-3 {
  background: #cd7f32;
  color: #fff;
}

.scenic-info {
  flex: 1;
  min-width: 0;
}

.scenic-name {
  font-size: 13px;
  font-weight: bold;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scenic-coord {
  font-size: 11px;
  color: #8aa4bd;
  margin-top: 3px;
}

.scenic-sales {
  font-size: 14px;
  color: #00d4ff;
  font-weight: bold;
  white-space: nowrap;
}

.rank-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rank-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rank-num {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  background: rgba(255, 255, 255, 0.1);
  flex: 0 0 22px;
}

.rank-num.num-1 {
  background: #ffd93d;
  color: #000;
}

.rank-num.num-2 {
  background: #c0c0c0;
  color: #000;
}

.rank-num.num-3 {
  background: #cd7f32;
  color: #fff;
}

.rank-main {
  flex: 1;
  min-width: 0;
}

.rank-name {
  font-size: 12px;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rank-bar {
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.rank-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #00d4ff, #00ff88);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.rank-value {
  width: 74px;
  text-align: right;
  font-size: 13px;
  color: #00d4ff;
  flex: 0 0 74px;
}

.rank-amount {
  font-size: 11px;
  color: #8aa4bd;
  margin-top: 2px;
}

.empty-tip {
  text-align: center;
  color: #888;
  padding: 20px;
  font-size: 12px;
}

/*
 * 说明：改为 1920×1080 设计稿 + transform: scale 等比缩放后，
 * 布局宽高永远按设计稿计算，不再需要用 vw 做响应式补偿，
 * 原先的 4:3 / 21:9 媒体查询已移除（否则会和设计稿比例冲突）。
 */

/* ===== 右下角显示比例微调：默认近乎隐形，鼠标移入才亮 ===== */
.screen-tool {
  /* 挂在 body 上，固定于窗口右下角：无论画面放大多少都能点到 */
  position: fixed;
  right: 10px;
  bottom: 10px;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.screen-tool-btn {
  width: 22px;
  height: 22px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 212, 255, 0.18);
  border-radius: 50%;
  background: rgba(0, 212, 255, 0.08);
  color: rgba(127, 150, 179, 0.75);
  opacity: 0.35;
  cursor: pointer;
  transition: opacity 0.2s, color 0.2s, background 0.2s;
}

/* 进页面后的短暂提示态：亮一点，提示这里可点 */
.screen-tool.hint .screen-tool-btn {
  opacity: 0.95;
  color: #00d4ff;
  background: rgba(0, 212, 255, 0.18);
  animation: st-pulse 1.6s ease-in-out 2;
}

@keyframes st-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(0, 212, 255, 0.45);
  }
  50% {
    box-shadow: 0 0 0 5px rgba(0, 212, 255, 0);
  }
}

.screen-tool:hover .screen-tool-btn,
.screen-tool-btn.active {
  opacity: 1;
  color: #00d4ff;
  background: rgba(0, 212, 255, 0.16);
}

.screen-tool-panel {
  width: 168px;
  padding: 8px 10px 9px;
  border-radius: 6px;
  background: rgba(6, 20, 34, 0.92);
  border: 1px solid rgba(0, 212, 255, 0.22);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
  font-size: 11px;
  color: #9fb8d0;
}

.st-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.st-label {
  flex-shrink: 0;
  color: #7f96b3;
}

.st-range {
  flex: 1;
  height: 3px;
  margin: 0;
  accent-color: #00d4ff;
  cursor: pointer;
}

.st-value {
  width: 34px;
  text-align: right;
  color: #00d4ff;
}

.st-actions {
  margin-top: 7px;
}

.st-btn {
  flex: 1;
  height: 20px;
  font-size: 11px;
  color: #9fb8d0;
  background: rgba(0, 212, 255, 0.08);
  border: 1px solid rgba(0, 212, 255, 0.18);
  border-radius: 4px;
  cursor: pointer;
}

.st-btn:hover {
  color: #00d4ff;
  border-color: rgba(0, 212, 255, 0.5);
}
</style>

<!-- 引线气泡挂在地图 floatPane 上，位于组件作用域之外，必须使用全局样式 -->
<style>
.dp-callout-box {
  position: absolute;
  z-index: 30;
  max-width: 260px;
  padding: 4px 20px 4px 8px;
  background: rgba(46, 10, 10, 0.88);
  border: 1px solid rgba(255, 77, 77, 0.75);
  border-radius: 4px;
  color: #ffe3e3;
  font-size: 11px;
  line-height: 1.5;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45);
  cursor: move;
  user-select: none;
}

.dp-callout-box.dragging {
  opacity: 0.85;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.6);
}

.dp-callout-box .cb-name {
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dp-callout-box .cb-val {
  color: #ffc9c9;
}

/* 右上角删除按钮 */
.dp-callout-box .cb-close {
  position: absolute;
  top: 1px;
  right: 2px;
  width: 14px;
  height: 14px;
  line-height: 13px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: #ffb3b3;
  border-radius: 2px;
  cursor: pointer;
}

.dp-callout-box .cb-close:hover {
  background: rgba(255, 77, 77, 0.45);
  color: #fff;
}

.dp-callout-line {
  position: absolute;
  z-index: 29;
  height: 1px;
  background: rgba(255, 77, 77, 0.75);
  pointer-events: none;
}
</style>
